/**
 * client/src/lib/use-switch-to-personal-then-navigate.ts
 *
 * #3268 review round 2 MAJOR 1 — shared, not duplicated: the "switch to
 * personal, then navigate" behaviour `held-key-posture-notice.tsx` already
 * has, extracted so `MasterHeldNextStep.tsx` (a SECOND surface needing the
 * identical remedy — the master-transfer control lives on the same
 * personal-only `/security-keys` page) reuses it rather than growing its own
 * copy of the same ordering/error contract.
 *
 * SWITCH BEFORE NAVIGATE, ALWAYS. A rejected switch must never move the
 * caller onto `href` while still wearing the org's acting context — mirrors
 * `contextual-entity-link.tsx`'s `navigateInTargetContext`, and the provider
 * itself restores the prior context after a rejected switch, so staying put
 * with an honest error (rather than a silent no-op) is correct.
 */
import { useState } from "react";
import { useLocation } from "wouter";
import { useOptionalOrgContext } from "@/lib/use-org-context";

export interface SwitchToPersonalThenNavigate {
  /** True while acting as an org — the caller renders its org-context
   *  affordance (a button wired to `run`) instead of a plain personal link. */
  inOrgContext: boolean;
  switching: boolean;
  /** Set only after a rejected switch; the caller stays on the current page. */
  error: string | null;
  /** Fire-and-forget; safe to pass straight to an `onClick`. */
  run: () => void;
}

export function useSwitchToPersonalThenNavigate(href: string): SwitchToPersonalThenNavigate {
  const org = useOptionalOrgContext();
  const [, setLocation] = useLocation();
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inOrgContext = org !== null && org.activeOrgId !== null;

  async function switchAndGo(): Promise<void> {
    if (org === null) return;
    setSwitching(true);
    setError(null);
    try {
      await org.switchToPersonal();
      setLocation(href);
    } catch {
      setError("Couldn't switch to your personal account. Try again.");
    } finally {
      setSwitching(false);
    }
  }

  return { inOrgContext, switching, error, run: () => { void switchAndGo(); } };
}
